import { LineChartWrapper } from '@modules/charts/components/LineChartWrapper';
import { DATE_RANGE_CONJUNCTION } from '@modules/common/components/DateRangePicker/constants';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  formatDateTimeRange,
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { QuantifyAnomaliesToggle } from '@modules/quantify/components/QuantifyAnomaliesToggle';
import { QuantifyCategoriesToggle } from '@modules/quantify/components/QuantifyCategoriesToggle';
import { QuantifyTrendChartTimePeriod } from '@modules/quantify/components/QuantifyChart/QuantifyTrendChart/QuantifyTrendChartTimePeriod';
import { QuantifyReportStatusWidget } from '@modules/quantify/components/QuantifyReportStatusWidget';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetChartLabelOptions,
  useGetFormattedDurationFieldLabels,
  useGetQuantifyChartData,
  useGetQuantifyFormulaDisplayName,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getNextDuration,
  getPreviousDuration,
  isAggregateFormula,
} from '@modules/quantify/utils';
import { Box, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { TimeRange } from 'v2/core/entities';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { QuantifyOrCompareChartTitle } from '../QuantifyOrCompareChartTitle';

const ORIGINAL_RELATIVE_DATE_END_BEFORE = 'Today';

const trackFeedbackListSummaryEvents =
  createAnalyticsTracker('timeRangeControls');

export interface QuantifyTrendChartProps {
  shouldShowToggleGroup?: boolean;
  shouldShowAddToFilter?: boolean;
  shouldShowTitle?: boolean;
  yAxisLabel?: string;
  shouldDisableInteraction?: boolean;
  shouldShowTimeRangeControls?: boolean;
  shouldShowLegendsInNewRow?: boolean;
  shouldDisableAnimation?: boolean;
  shouldDisableCategoryToggle?: boolean;
  shouldDisableAnomaliesToggle?: boolean;
  shouldShowLegends?: boolean;
  shouldAutoAdjustHeight?: boolean;
}

export function QuantifyTrendChart({
  shouldShowAddToFilter = true,
  shouldShowTitle: shouldShowTitleDefault = true,
  shouldDisableInteraction = false,
  shouldShowTimeRangeControls = true,
  shouldShowToggleGroup = true,
  yAxisLabel,
  shouldShowLegendsInNewRow = false,
  shouldDisableAnimation = false,
  shouldDisableCategoryToggle = false,
  shouldDisableAnomaliesToggle = false,
  shouldShowLegends = true,
  shouldAutoAdjustHeight = false,
}: QuantifyTrendChartProps) {
  const data = useGetQuantifyChartData();
  const fetchReportData = useFetchQuantifyReport();
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const valueRange = useQuantifyStore(
    ({ reportData }) => reportData.quantify.valueRange,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause ?? EmptyWhereClause,
  );
  const aggregatePeriod = useQuantifyStore(
    ({ metadata }) => metadata.aggregatePeriod,
  );
  const sortBy = useQuantifyStore(
    ({ metadata }) => metadata.sortBy ?? DEFAULT_QUANTIFY_SORT_BY,
  );
  const traverseCount = useQuantifyStore(
    ({ visual }) => visual.timeRangeTraversal.traverseCount,
  );
  const originalDuration = useQuantifyStore(
    ({ visual }) => visual.timeRangeTraversal.originalDuration,
  );
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const { durationLabel } = useGetFormattedDurationFieldLabels({
    shouldShowForAllDurationTypes: true,
  });

  useEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [shouldFetchReportData, fetchReportData]);

  const getLabelOptions = useGetChartLabelOptions({ data });
  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName();

  const isSingleRecordQuery = useIsSingleRecordQuery();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const timestamps = useMemo(() => {
    const timestampsSet = new Set<number>();
    data?.forEach((dataPoint) => {
      if (dataPoint.timestamp && !timestampsSet.has(dataPoint.timestamp)) {
        timestampsSet.add(dataPoint.timestamp);
      }
    });
    return [...timestampsSet];
  }, [data]);

  const isDataEmpty = !data.length;
  const shouldShowTitle = shouldShowTitleDefault && !isDataEmpty;

  const getYAxisLabel = () => {
    if (yAxisLabel) {
      return yAxisLabel;
    }

    const formulaUnit =
      formulaID === EQLFunctionName.NormaliseByPercent ? '%' : '#';

    const eqlFieldDisplayName = getFormattedDisplayNameForEqlField({
      eqlField: measuredAsCriteria,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
    });

    if (isAggregateFormula(formulaID)) {
      return `${eqlFieldDisplayName} : ${formulaDisplayName}`;
    }

    return `${formulaUnit} ${eqlFieldDisplayName}`;
  };

  const getYAxisDomain = () => {
    if (valueRange) {
      return [valueRange?.start, valueRange?.end];
    }

    return undefined;
  };

  const onPrevClick = () => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration:
          traverseCount - 1 === 0
            ? originalDuration
            : getPreviousDuration(duration, aggregatePeriod),
      },
    });
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        timeRangeTraversal: {
          originalDuration,
          traverseCount: traverseCount - 1,
        },
      },
    });
    setShouldFetchReportData(true);
    trackFeedbackListSummaryEvents({
      eventName: 'Time Range Controls - Pervious Button Clicked',
      originalDuration: formatDateTimeRange(originalDuration),
      currentDuration: formatDateTimeRange(
        traverseCount - 1 === 0
          ? originalDuration
          : getPreviousDuration(duration, aggregatePeriod),
      ),
    });
  };

  const onNextClick = () => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration:
          traverseCount + 1 === 0
            ? originalDuration
            : getNextDuration(duration, aggregatePeriod),
      },
    });
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        timeRangeTraversal: {
          originalDuration,
          traverseCount: traverseCount + 1,
        },
      },
    });
    setShouldFetchReportData(true);
    trackFeedbackListSummaryEvents({
      eventName: 'Time Range Controls - Next Button Clicked',
      originalDuration: formatDateTimeRange(originalDuration),
      currentDuration: formatDateTimeRange(
        traverseCount + 1 === 0
          ? originalDuration
          : getNextDuration(duration, aggregatePeriod),
      ),
    });
  };

  const getIsNextDisabled = () => {
    const nextDuration = getNextDuration(duration, aggregatePeriod);
    const now = dayjs().tz();
    const endOfToday = now.clone().endOf('day').tz();

    return (
      nextDuration.startAfter?.isAfter(now) ||
      nextDuration.endBefore?.isAfter(endOfToday)
    );
  };

  const isNextDisabled = getIsNextDisabled();
  const secondaryXAxisLabel =
    (traverseCount && durationLabel.secondary) ||
    originalDuration.type === 'absolute'
      ? durationLabel.secondary ?? ''
      : `${
          durationLabel.secondary?.split(DATE_RANGE_CONJUNCTION)[0]
        }${DATE_RANGE_CONJUNCTION}${ORIGINAL_RELATIVE_DATE_END_BEFORE}`;

  return (
    <Box height="100%">
      <Stack
        direction="row"
        justifyContent={shouldShowTitle ? 'space-between' : 'flex-end'}
        alignItems="center"
        mb={!shouldShowTitle && !shouldShowToggleGroup ? 0 : 2}
      >
        {shouldShowTitle && (
          <QuantifyOrCompareChartTitle
            data={data}
            pl={2}
            getLabelOptions={getLabelOptions}
          />
        )}
        {shouldShowToggleGroup && (
          <Stack
            direction="row-reverse"
            justifyContent="space-between"
            alignItems="center"
            spacing={3}
            gap={1.5}
            flexWrap="wrap"
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={3}
            >
              {shouldDisableCategoryToggle ? null : (
                <QuantifyCategoriesToggle />
              )}
              {shouldDisableAnomaliesToggle ? null : (
                <QuantifyAnomaliesToggle />
              )}
            </Stack>
            <QuantifyTrendChartTimePeriod />
          </Stack>
        )}
      </Stack>
      {!isDataEmpty ? (
        <LineChartWrapper
          baseWhereClause={whereClause}
          data={data}
          timestamps={timestamps}
          fieldName={groupBy ? getFieldNameFromEqlField(groupBy) : undefined}
          qbField={
            groupBy ? getMetadataQBFieldFromEqlField(groupBy) : undefined
          }
          displayName={
            groupBy
              ? getFormattedDisplayNameForEqlField({
                  eqlField: groupBy,
                  shouldRemoveFileUploadPrefix,
                  isUnifiedFieldEnabled,
                })
              : undefined
          }
          dateRange={duration as TimeRange}
          aggregatePeriod={aggregatePeriod}
          xAxis={{
            dataKey: 'dateRange',
            labelData: {
              primary: 'Time-Range',
              secondary: secondaryXAxisLabel,
            },
            size: 56,
            onPrevClick,
            onNextClick,
            isNextDisabled,
            shouldShowTimeRangeControls,
          }}
          yAxis={{
            label: getYAxisLabel(),
            domain: getYAxisDomain(),
          }}
          shouldShowAddToFilter={shouldShowAddToFilter}
          shouldShowActionTooltip={!shouldDisableInteraction}
          shouldShowInfoTooltip={!shouldDisableInteraction}
          sortBy={sortBy}
          shouldShowLegendsInNewRow={shouldShowLegendsInNewRow}
          shouldAutoAdjustHeight={shouldAutoAdjustHeight}
          shouldDisableAnimation={shouldDisableAnimation}
          getLabelOptions={getLabelOptions}
          onMergeComplete={fetchReportData}
          shouldShowLegends={shouldShowLegends}
          shouldExcludeDataPointWhereClause={isAggregateFormula(formulaID)}
          formulaID={formulaID}
          refetch={fetchReportData}
          measuredAsCriteria={measuredAsCriteria}
          isSingleRecordQuery={isSingleRecordQuery}
        />
      ) : (
        <QuantifyReportStatusWidget />
      )}
    </Box>
  );
}
